import React from 'react';
import { Calendar } from 'lucide-react';
import { useFormContext, Controller } from 'react-hook-form';
import { CreatableCombobox } from '../../../components/common/CreatableCombobox';
import { countries } from '../../../data/countries';
import { type CreateGreenBeanProfileDTO } from '../../../types/greenBean';
import { useLanguage } from '../../../features/language/useLanguage';
import { openNativeDatePickerSafely, preventManualDateEntry, preventManualDatePasteOrDrop } from '../../../utils/date';

interface GreenBeanOriginSectionProps {
  uniqueOptions: {
    regions: string[];
    farms: string[];
    producers: string[];
    species: string[];
    varieties: string[];
  };
  disabled?: boolean;
}

export const GreenBeanOriginSection: React.FC<GreenBeanOriginSectionProps> = ({ uniqueOptions, disabled = false }) => {
  const { t } = useLanguage();
  const { control, register } = useFormContext<{ green_bean_profile: CreateGreenBeanProfileDTO }>();

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">{t('greenBean.origin.title')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Controller
            control={control}
            name="green_bean_profile.country"
            render={({ field }) => (
              <CreatableCombobox
                label={t('greenBean.extrinsic.country')}
                value={field.value}
                onChange={field.onChange}
                options={countries}
                placeholder={t('greenBean.extrinsic.placeholderCountry')}
                dataTestId="green-bean-country-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.region"
            render={({ field }) => (
              <CreatableCombobox
                label={t('greenBean.extrinsic.region')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.regions}
                placeholder={t('greenBean.extrinsic.placeholderRegion')}
                dataTestId="green-bean-region-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.farm_name"
            render={({ field }) => (
              <CreatableCombobox
                label={t('greenBean.extrinsic.farm')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.farms}
                placeholder={t('greenBean.extrinsic.placeholderFarm')}
                dataTestId="green-bean-farm-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.producer"
            render={({ field }) => (
              <CreatableCombobox
                label={t('inventory.modal.fields.producer')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.producers}
                placeholder={t('inventory.modal.fields.producerPlaceholder')}
                dataTestId="green-bean-producer-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.species"
            render={({ field }) => (
              <CreatableCombobox
                label={t('inventory.modal.fields.species')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.species}
                placeholder={t('inventory.modal.fields.speciesPlaceholder')}
                dataTestId="green-bean-species-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <Controller
            control={control}
            name="green_bean_profile.variety"
            render={({ field }) => (
              <CreatableCombobox
                label={t('inventory.modal.fields.variety')}
                value={field.value}
                onChange={field.onChange}
                options={uniqueOptions.varieties}
                placeholder={t('inventory.modal.fields.varietyPlaceholder')}
                dataTestId="green-bean-variety-input"
                disabled={disabled}
              />
            )}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.extrinsic.harvestDateYear')}</label>
          <div className="relative">
            <input
              type="date"
              disabled={disabled}
              {...register('green_bean_profile.harvest_date')}
              onClick={openNativeDatePickerSafely}
              onKeyDown={preventManualDateEntry}
              onPaste={preventManualDatePasteOrDrop}
              onDrop={preventManualDatePasteOrDrop}
              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${
                disabled ? 'bg-gray-100 text-transparent' : ''
              }`}
              data-test-id="green-bean-harvest-date-input"
            />
            {disabled && (
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Calendar className="h-4 w-4 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700">{t('greenBean.origin.notes')}</label>
          <textarea
            disabled={disabled}
            {...register('green_bean_profile.origin_notes')}
            rows={3}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2 ${disabled ? 'bg-gray-100' : ''}`}
            data-test-id="green-bean-origin-notes-input"
          />
        </div>
      </div>
    </div>
  );
};
